import { Button, type buttonVariants } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Tooltip } from "@/components/ui/Tooltip";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import {
  useAudioTrack,
  useDaily,
  useDevices,
  useLocalSessionId,
  useMeetingState,
  useParticipantCounts,
  useVideoTrack,
} from "@daily-co/daily-react";
import type { VariantProps } from "class-variance-authority";
import { useAtomValue } from "jotai";
import {
  CircleSlash,
  Languages,
  LogOut,
  Mic,
  MicOff,
  Users,
  Video,
  VideoOff,
} from "lucide-react";
import type React from "react";
import {
  isDialingTranslatorAtom,
  translatorIdAtom,
} from "../atoms/videoCallAtoms";
import { useTranslatorOptions } from "../hooks/useTranslatorOptions";
import type { TranslatorOption } from "../utils/translatorOptions";
import { RecordingConsentIndicator } from "./RecordingConsentIndicator";

interface TrayProps {
  leaveCall: () => void;
  addTranslator: (option?: TranslatorOption) => void;
  removeTranslator: () => void;
}

// Helper for TrayButton functionality
const TrayButtonInternal = ({
  onClick,
  Icon,
  text,
  ariaLabel,
  variant = "secondary",
  availableDevices,
  currentDevice,
  onChange,
  disabled,
}: {
  onClick?: () => void;
  Icon: React.ElementType;
  text: string;
  // Defaults to `text`. Pass explicitly when the tooltip text changes (e.g.
  // to reflect in-progress state) but queries/assistive tech need a stable name.
  ariaLabel?: string;
  variant?: VariantProps<typeof buttonVariants>["variant"];
  // Using MediaDeviceInfo based on useDevices hook return type
  availableDevices?: MediaDeviceInfo[];
  currentDevice?: MediaDeviceInfo | null;
  onChange?: (deviceId: string) => void;
  disabled?: boolean;
}) => (
  <div className="flex items-center gap-1">
    <Tooltip tooltipContentText={text} side="top">
      <Button
        onClick={onClick}
        variant={variant}
        size="icon"
        className="h-6 w-6"
        aria-label={ariaLabel ?? text}
        disabled={disabled}
      >
        <Icon size={12} />
      </Button>
    </Tooltip>
    {availableDevices && onChange && (
      <Select defaultValue={currentDevice?.deviceId} onValueChange={onChange}>
        <SelectTrigger className="w-[100px] h-6 text-xs px-1">
          <SelectValue placeholder={currentDevice?.label || "Select device"} />
        </SelectTrigger>
        <SelectContent>
          {availableDevices.map((device) => (
            <SelectItem
              key={device.deviceId}
              value={device.deviceId}
              className="text-xs" // Adjusted styling
            >
              {/* Truncate long device names */}
              <span className="truncate block">{device.label}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )}
  </div>
);

// Dropdown for picking which LanguageLine interpreter to dial into the call
const AddTranslatorTrayButton = ({
  options,
  addTranslator,
  disabled,
  tooltipText,
}: {
  options: TranslatorOption[];
  addTranslator: (option: TranslatorOption) => void;
  disabled: boolean;
  tooltipText: string;
}) => (
  <DropdownMenu>
    <Tooltip tooltipContentText={tooltipText} side="top">
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          variant="secondary"
          size="icon"
          className="h-6 w-6"
          aria-label="Add Translator"
        >
          <Languages size={12} />
        </Button>
      </DropdownMenuTrigger>
    </Tooltip>
    <DropdownMenuContent side="top">
      <DropdownMenuLabel className="text-xs">Select language</DropdownMenuLabel>
      {options.map((option) => (
        <DropdownMenuItem
          key={option.sipUri}
          className="text-xs"
          onSelect={() => addTranslator(option)}
        >
          {option.label}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

// Helper for ParticipantCount functionality
const ParticipantCountTrayButton = ({
  participantCount,
}: { participantCount: number }) => (
  <div className="flex flex-col items-center gap-1">
    <Tooltip
      tooltipContentText={`Participants: ${participantCount}`}
      side="top"
    >
      <Button
        variant="secondary"
        size="icon"
        className="h-6 w-6 cursor-default" // Make non-interactive visually
        aria-label={`Participants: ${participantCount}`}
      >
        <Users size={12} />
      </Button>
    </Tooltip>
  </div>
);

export const Tray = ({
  leaveCall,
  addTranslator,
  removeTranslator,
}: TrayProps) => {
  const { data: languageLineAddress } = useConfigCatFeatureFlag(
    "languageline-address",
  );
  // Per-clinician rollout gate for the recording-consent feature. Until it is
  // enabled for this clinician, hide the indicator entirely (matches the
  // server-side `av-call-recording-consent-emr` gate on AV-call
  // post-processing).
  const { data: recordingConsentEnabled } = useConfigCatFeatureFlag(
    "av-call-recording-consent-emr",
  );
  const translatorOptions = useTranslatorOptions();
  const translatorId = useAtomValue(translatorIdAtom);
  const isDialingTranslator = useAtomValue(isDialingTranslatorAtom);
  // Daily only allows dialing out once joined; the call may never join if,
  // e.g., camera/mic permission is blocked
  const isJoined = useMeetingState() === "joined-meeting";
  const addTranslatorDisabled = !isJoined || isDialingTranslator;
  // Tooltip reflects the in-flight dial-out; aria-label stays fixed as
  // "Add Translator" so existing queries/assistive tech aren't affected.
  const addTranslatorTooltipText = isDialingTranslator
    ? "Connecting interpreter…"
    : "Add Translator";

  const callObject = useDaily();
  const participants = useParticipantCounts();

  const localSessionId = useLocalSessionId();
  const localVideoState = useVideoTrack(localSessionId ?? "");
  const localAudioState = useAudioTrack(localSessionId ?? "");
  const {
    cameras,
    microphones,
    setCamera,
    setMicrophone,
    currentCam,
    currentMic,
  } = useDevices();

  const isMicMuted = localAudioState.isOff ?? true;
  const isCamOff = localVideoState.isOff ?? true;

  const handleMicToggle = () => {
    if (callObject) {
      callObject.setLocalAudio(isMicMuted);
    }
  };

  const handleCamToggle = () => {
    if (callObject) {
      callObject.setLocalVideo(isCamOff);
    }
  };

  const handleMicChange = (deviceId: string) => {
    setMicrophone(deviceId);
  };

  const handleCamChange = (deviceId: string) => {
    setCamera(deviceId);
  };

  // Mimic legacy CSS using Tailwind. `flex-wrap` + row gap lets the controls
  // flow onto additional rows on narrow screens so none get clipped/cut off;
  // on wide screens everything still fits on a single row.
  const trayContainerClasses =
    "z-10 relative bg-white flex flex-wrap justify-around items-center gap-y-2 p-2 border-b border-gray-300 min-h-[40px]"; // Use min-height, adjust padding/items alignment

  return (
    <div className={trayContainerClasses}>
      {recordingConsentEnabled && <RecordingConsentIndicator />}
      <TrayButtonInternal
        onClick={() => handleMicToggle()}
        Icon={isMicMuted ? MicOff : Mic}
        text={isMicMuted ? "Unmute" : "Mute"}
        variant={isMicMuted ? "danger" : "secondary"}
        availableDevices={microphones?.map((m) => m.device)}
        currentDevice={currentMic?.device}
        onChange={(deviceId) => handleMicChange(deviceId)}
      />
      <TrayButtonInternal
        onClick={() => handleCamToggle()}
        Icon={isCamOff ? VideoOff : Video}
        text={isCamOff ? "Turn on camera" : "Turn off camera"}
        variant={isCamOff ? "danger" : "secondary"}
        availableDevices={cameras?.map((c) => c.device)}
        currentDevice={currentCam?.device}
        onChange={(deviceId) => handleCamChange(deviceId)}
      />
      <ParticipantCountTrayButton participantCount={participants.present} />
      {translatorId ? (
        <TrayButtonInternal
          onClick={removeTranslator}
          Icon={CircleSlash}
          text="Remove Translator"
          variant="danger"
        />
      ) : translatorOptions ? (
        <AddTranslatorTrayButton
          options={translatorOptions}
          addTranslator={addTranslator}
          disabled={addTranslatorDisabled}
          tooltipText={addTranslatorTooltipText}
        />
      ) : (
        // Until `languageline-translator-options` is configured, fall back to
        // dialing the single `languageline-address` interpreter.
        languageLineAddress && (
          <TrayButtonInternal
            onClick={() => addTranslator()}
            Icon={Languages}
            text={addTranslatorTooltipText}
            ariaLabel="Add Translator"
            variant="secondary"
            disabled={addTranslatorDisabled}
          />
        )
      )}
      <TrayButtonInternal
        onClick={leaveCall}
        Icon={LogOut}
        text="Leave"
        variant="danger"
      />
    </div>
  );
};
